import { useMemo } from 'react';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';
import type { DecisionMaker } from 'src/app/pages/petitionDetails/shared/types';

import type { DecisionMakerAndLegislativeBody } from '../../../../hooks/dmsWithLegislativeBodies';
import { useDmsWithLegislativeBodiesConnection } from '../../../decisionMakers/hooks/useDmsWithLegislativeBodiesConnection';
import { usePrimaryDecisionMakerConnection } from '../../../decisionMakers/hooks/usePrimaryDecisionMakerConnection';

type Result = ModelHookResult<{
	isShown: boolean;
	count: number;
	first: { displayName: string };
	dms: DecisionMaker[] | Array<DecisionMakerAndLegislativeBody['decisionMakers'][number]>;
}>;

export const useDecisionMakers = (): Result => {
	const {
		data: {
			featureConfigs: { dmWithLegislativeBodiesConnectionEnabled },
		},
	} = usePetitionDetailsPageData();
	const { data: decisionMakersConnection } = usePrimaryDecisionMakerConnection();
	const { data: dmsAndLBsConnection } = useDmsWithLegislativeBodiesConnection();

	const isShown = decisionMakersConnection.totalCount > 0 || dmsAndLBsConnection.totalCount > 0;
	const count = decisionMakersConnection.totalCount || dmsAndLBsConnection.totalCount;
	const first = useMemo(() => {
		if (dmWithLegislativeBodiesConnectionEnabled) {
			if (dmsAndLBsConnection.totalCount > 0) {
				return dmsAndLBsConnection.decisionMakersAndLegislativeBodies[0].decisionMakers[0];
			}
			return { displayName: '' };
		}
		return decisionMakersConnection.totalCount > 0 ? decisionMakersConnection.decisionMakers[0] : { displayName: '' };
	}, [decisionMakersConnection, dmsAndLBsConnection, dmWithLegislativeBodiesConnectionEnabled]);

	const dms = useMemo(() => {
		if (dmWithLegislativeBodiesConnectionEnabled) {
			const list = [];
			for (const item of dmsAndLBsConnection.decisionMakersAndLegislativeBodies) {
				for (const dm of item.decisionMakers) {
					list.push(dm);
				}
			}
			return list;
		}
		return decisionMakersConnection.totalCount > 0 ? decisionMakersConnection.decisionMakers : [];
	}, [decisionMakersConnection, dmsAndLBsConnection, dmWithLegislativeBodiesConnectionEnabled]);

	const decisionMakers = useMemo(() => {
		return {
			data: {
				isShown,
				count,
				first,
				dms,
			},
		};
	}, [isShown, count, first, dms]);

	return decisionMakers;
};
